import { useApi } from "@platform/api";
import { useQuery } from "@tanstack/react-query";

import { listCarrierReps } from "../api/client";

import { CARRIERS_QUERY_KEY } from "./useCarriersQuery";

import type { CarrierRepListResponse, CarrierRepResponse } from "../api/types";
import type { CarrierRep, CarrierRepList } from "../types";

const LOAD_CARRIER_REPS_ERROR_MESSAGE = "Unable to load reps.";

export const getCarrierRepsQueryKey = (carrierId: number): unknown[] => [
  ...CARRIERS_QUERY_KEY,
  carrierId,
  "reps",
];

interface UseCarrierRepsQueryResult {
  repList: CarrierRepList | null;
  loading: boolean;
  error: string | null;
}

const mapCarrierRep = (rep: CarrierRepResponse): CarrierRep => ({
  id: rep.id,
  name: rep.name,
  title: rep.title,
  email: rep.email,
  phone: rep.phone,
  notes: rep.notes,
  type: rep.type,
  active: rep.active,
  brokers: [],
});

export const useCarrierRepsQuery = (
  carrierId: number,
): UseCarrierRepsQueryResult => {
  const axiosInstance = useApi();

  const query = useQuery<CarrierRepListResponse, Error, CarrierRepList>({
    queryKey: getCarrierRepsQueryKey(carrierId),
    queryFn: () => listCarrierReps(axiosInstance, carrierId),
    select: (response) => ({
      reps: response.carrierReps.map(mapCarrierRep),
      activeRepsCount: response.activeRepsCount,
      lastEditedAt: response.lastEditedAt,
    }),
  });

  return {
    repList: query.data ?? null,
    loading: query.isFetching,
    error: query.error ? LOAD_CARRIER_REPS_ERROR_MESSAGE : null,
  };
};
